/**
 * Modelo del cotizador unificado de remesas.
 *
 * Normaliza en UN solo view-model dos contratos de backend incompatibles:
 *  · rieles propios (CLP↔VES, USDT) → devuelven un MULTIPLICADOR y el front
 *    hace la multiplicación;
 *  · rieles Vita (COP, PEN) → devuelven el RESULTADO ya calculado, más
 *    mínimos, máximos y disponibilidad.
 *
 * Nada de este modelo se inventa: todo sale de una respuesta del backend.
 */

/** Moneda con la que paga el cliente. */
export type OrigenRemesa = 'CLP' | 'VES' | 'USDT';

/** País destino del envío. */
export type DestinoRemesa = 'VE' | 'CL' | 'CO' | 'PE';

/** Riel de entrega en Colombia: Bre-B (instantáneo) o bancario. */
export type RielColombia = 'COBREB' | 'CO';

/** Perfil del usuario según `tipoId`. */
export type PerfilUsuario = 'CL' | 'VE';

/** Qué libreta de beneficiarios aplica al destino. */
export type TipoLibreta = 'VENEZUELA' | 'CHILE' | 'VITA';

export interface DestinoMeta {
  code: DestinoRemesa;
  nombre: string;
  moneda: string;
  flag: string;
  libreta: TipoLibreta;
}

/**
 * Cotización normalizada. Es lo único que la UI conoce: ningún componente
 * habla directamente con un servicio de tasas.
 */
export interface CotizacionVM {
  origen: OrigenRemesa;
  destino: DestinoRemesa;
  monedaOrigen: string;
  monedaDestino: string;

  /** Monto que el usuario paga, en `monedaOrigen`. */
  monto: number;
  /** Monto que recibe el beneficiario, en `monedaDestino`. */
  recibe: number | null;

  /** Unidades de destino por 1 de origen. Es la que se muestra. */
  tasaEfectiva: number | null;
  /** Tasa bruta antes de comisión. `null` = el riel no publica referencia. */
  tasaReferencia: number | null;

  /**
   * Comisión en moneda de ORIGEN. `null` cuando el riel no cobra o cuando
   * faltan datos para derivarla honestamente.
   */
  comision: number | null;
  /** El riel es genuinamente sin comisión (sólo CLP↔VES y USDT propios). */
  sinComision: boolean;

  min: number | null;
  max: number | null;
  bajoMinimo: boolean;
  excedeMaximo: boolean;

  /** ¿Se puede cotizar? `false` = no hay tasa o el riel no responde. */
  disponible: boolean;
  /** ¿Se puede enviar ahora? Cotiza igual aunque sea `false`. */
  activo: boolean;
  /** Motivo legible cuando no se puede operar. */
  motivo: string | null;

  businessDays: number | null;
  etaLabel: string;
  /** Máscara de decimales del monto destino (`DecimalPipe`). */
  decimales: string;

  /** IDs de tasa que algunos envíos propios necesitan reenviar. */
  tasaCambioId?: number | null;
  valorCambioId?: number | null;

  /**
   * Clave de la franja (tramo por monto) que eligió el servidor para este
   * monto: `rangoNumerico` de `por-monto`. Sólo los rieles propios CLP↔VES la
   * tienen; `null` o ausente en los demás. Sirve para dos cosas: no honrar la
   * tasa vista si el monto cambió de franja al confirmar (`decisionTasa`) y
   * detectar que el cálculo inverso cruzó un corte (`cruceDeFranja`).
   */
  franja?: string | null;

  /**
   * Instante en que ESTA cotización volvió del backend. No es lo mismo que el
   * momento de confirmar: es el momento en que el usuario vio el número, que
   * es lo que hay que reportar cuando se honra una tasa.
   */
  cotizadoEn?: number;
}

/**
 * Foto de la cotización en el momento en que el usuario confirmó. Es la
 * referencia contra la que se detecta un cambio de tasa antes de enviar.
 */
export interface SnapshotCotizacion {
  origen: OrigenRemesa;
  destino: DestinoRemesa;
  monto: number;
  recibe: number;
  tasaEfectiva: number;
  tomadoEn: number;

  /**
   * IDs de la tasa que el usuario vio. Van en el snapshot y no sólo en la
   * cotización porque cuando se honra la tasa mostrada, el envío tiene que
   * reenviar ESTOS ids: son los que hacen coherente el trío que valida el
   * backend contra la fila de `rates_calculated`.
   */
  tasaCambioId?: number | null;
  valorCambioId?: number | null;

  /**
   * Franja de la tasa que el usuario vio (`CotizacionVM.franja`). En CLP→VES
   * honrar manda la fila de ESTA franja, y la lambda valida el monto contra
   * los cortes vigentes de esa franja: si el monto ya cae en otra, descarta la
   * orden en silencio. Por eso un cambio de franja siempre pide reconfirmar.
   */
  franja?: string | null;

  /** Cuándo se cotizó lo que el usuario vio (≠ `tomadoEn`, que es al confirmar). */
  cotizadoEn?: number;
}

/**
 * Se honró la tasa que el usuario vio en vez de la vigente: el movimiento
 * favorecía a Aguacate (o empataba), así que no se mostró nada.
 *
 * No se persiste desde el front. El rastro auditable ya queda en la base
 * —`transacciones.tasa`, `tasa_cambio_id` y `created_at`, cruzados con
 * `rates_calculated`, que es append-only y fechado— así que `costo_pct` se
 * reconstruye por consulta para cualquier transacción. Esto es para el evento
 * de diagnóstico del front, no para compliance.
 */
export interface AjusteTasaHonrada {
  origen: OrigenRemesa;
  destino: DestinoRemesa;
  monto: number;
  /** Monto en VES que vio el usuario y que efectivamente se envía. */
  recibeMostrado: number;
  /** Monto en VES que habría dado la tasa vigente al confirmar. */
  recibeVigente: number;
  /** Con signo. Negativo = el movimiento favoreció a Aguacate. */
  costoVes: number;
  costoPct: number | null;
  tasaCambioId: number | null;
  cotizadoEn: number | null;
  confirmadoEn: number;
}

/** Beneficiario normalizado: una sola lista para tres libretas distintas. */
export interface BeneficiarioVM {
  /** Identificador que va en el DTO de envío. Su tipo depende de la libreta. */
  id: string | number;
  libreta: TipoLibreta;
  titulo: string;
  subtitulo: string;
  /** Etiquetas cortas: "Pago Móvil", "Bre-B", nombre del banco. */
  badges: string[];
  /** No se puede seleccionar (límite de Pago Móvil, beneficiario baneado). */
  bloqueado: boolean;
  motivoBloqueo: string | null;
  /** Entidad original, para construir el DTO sin perder campos. */
  raw: any;
}

/**
 * La tasa cambió entre que el usuario confirmó y el envío. NO se envió nada:
 * el usuario tiene que volver a confirmar con los valores nuevos.
 */
export interface CambioDeTasa {
  cambio: true;
  mensaje: string;
  recibeAnterior: number;
  cotizacion: CotizacionVM;
}

/** Envío aceptado por el backend. */
export interface EnvioOk {
  ok: true;
  referencia: string | number | null;
  estado: string;
  recibe: number;
  monedaDestino: string;
  /**
   * Tasa que el servidor APLICÓ, si la respuesta la trae (swap USDT→VES:
   * `swap.rate`, la menor entre la cotizada y la del tramo). El comprobante la
   * prefiere a la cotizada.
   */
  tasaAplicada?: number;
}

/** Envío rechazado con un motivo accionable por el usuario. */
export interface EnvioError {
  ok: false;
  mensaje: string;
  /** Deriva a un operador de Aguacate en vez de ofrecer autoservicio. */
  contactarOperador: boolean;
  /**
   * El resultado es AMBIGUO: la petición pudo haber llegado. NUNCA ofrecer
   * "reintentar" a ciegas — hay que verificar en el historial primero.
   */
  ambiguo: boolean;
  /**
   * El backend detectó un envío casi idéntico reciente. No se envió nada: el
   * usuario tiene que confirmar explícitamente que es intencional.
   */
  posibleDuplicado?: boolean;
  /**
   * El servidor rechazó la tasa enviada (`esRechazoPorTasa`: 409 en VES→CLP, 400 en el swap USDT→VES y en la lambda). No se creó
   * nada: la pantalla vuelve a cotizar y el usuario confirma de nuevo.
   */
  recotizar?: boolean;
}

export type ResultadoEnvio = EnvioOk | EnvioError | CambioDeTasa;

export function esCambioDeTasa(r: ResultadoEnvio): r is CambioDeTasa {
  return (r as CambioDeTasa).cambio === true;
}

export function esEnvioOk(r: ResultadoEnvio): r is EnvioOk {
  return (r as EnvioOk).ok === true;
}

/**
 * Comprobante de un envío aceptado. Es una FOTO: se construye en el instante
 * en que el backend confirmó y no vuelve a leer estado vivo del componente.
 *
 * Existe por eso: la pantalla de éxito comparte lo que se envió, no lo que el
 * cotizador tenga cargado cuando el usuario toca el botón. Si leyera estado
 * vivo, un "Hacer otro envío" a medias —o una re-cotización en background—
 * podría emitir un comprobante con números que nunca se enviaron.
 *
 * Los formatos (`fmt*`) viajan con los datos para que el texto diga EXACTAMENTE
 * lo mismo que la pantalla: son las mismas máscaras de `DecimalPipe` que usa el
 * resumen, no un formateo aparte.
 *
 * NUNCA lleva el nombre del proveedor del riel: para el cliente el envío es de
 * Aguacate. Ver `ocultarProveedor()` en `rules/remittance-receipt.ts`.
 */
export interface ComprobanteEnvio {
  referencia: string | number | null;
  /** Instante en que el envío quedó confirmado (epoch ms, hora local). */
  enviadoEn: number;

  /** País destino, legible: "Venezuela", "Colombia". */
  pais: string;
  monedaOrigen: string;
  monedaDestino: string;
  monto: number;
  recibe: number;

  /** La ÚNICA tasa que se le mostró al cliente. `null` = el riel no publica. */
  tasa: number | null;
  etiquetaTasa: string;

  /** Lo que da la tasa antes de la comisión, en moneda de DESTINO. */
  subtotal: number | null;
  /** Comisión en moneda de DESTINO: `subtotal − comisión = recibe`. */
  comision: number | null;
  sinComision: boolean;

  beneficiario: string;
  cuenta: string;
  /** Banco destino, si la libreta lo informa y no es el nombre del proveedor. */
  banco: string | null;
  /** Cómo se entrega: "Bre-B", "Pago Móvil", "Transferencia bancaria". */
  metodo: string | null;

  eta: string;

  fmtDestino: string;
  fmtTasa: string;
  fmtComision: string;
}
